import React, { useMemo, useCallback, useState } from 'react';
import {
  startOfDay,
  addDays,
  addMinutes,
  format,
  isSameDay,
  isBefore,
  startOfWeek,
  eachDayOfInterval,
  setHours,
  setMinutes,
} from 'date-fns';

// Type definitions
export interface AvailabilityWindow {
  start: number; // UTC timestamp ms
  end: number; // UTC timestamp ms
}

export interface AvailabilityPickerProps {
  event: {
    timeframe: string;
    timeframeStart?: number;
    timeframeEnd?: number;
    duration: string;
    customDuration?: { hours: number; minutes: number };
  };
  selectedSlots: AvailabilityWindow[];
  onSlotsChange: (slots: AvailabilityWindow[]) => void;
  onSubmit: () => void;
  isEditing: boolean;
  mode?: 'single' | 'group';
  organizerName?: string;
}

interface TimeSlot {
  start: Date;
  end: Date;
  label: string;
  isPast: boolean;
  dateKey: string;
}

interface DayGroup {
  date: Date;
  label: string;
  dateLabel: string;
  slots: TimeSlot[];
}

// Constants
const SLOT_DURATION_MINUTES = 30;
const DAY_START_HOUR = 9; // 9am
const DAY_END_HOUR = 21; // 9pm

/**
 * Generate time slots for a given day
 */
const generateSlotsForDay = (date: Date, now: Date): TimeSlot[] => {
  const slots: TimeSlot[] = [];
  const dayStart = setMinutes(setHours(date, DAY_START_HOUR), 0);
  const dayEnd = setMinutes(setHours(date, DAY_END_HOUR), 0);

  let currentSlot = dayStart;

  while (isBefore(currentSlot, dayEnd)) {
    const slotEnd = addMinutes(currentSlot, SLOT_DURATION_MINUTES);
    const isPast = isBefore(currentSlot, now);

    slots.push({
      start: currentSlot,
      end: slotEnd,
      label: format(currentSlot, 'h:mm a'),
      isPast,
      dateKey: format(currentSlot, 'yyyy-MM-dd'),
    });

    currentSlot = slotEnd;
  }

  return slots;
};

/**
 * Generate days based on timeframe
 */
const generateDays = (
  timeframe: string,
  timeframeStart?: number,
  timeframeEnd?: number
): Date[] => {
  const now = new Date();
  const today = startOfDay(now);

  switch (timeframe) {
    case 'today':
      return [today];

    case 'tomorrow':
      return [addDays(today, 1)];

    case 'this_weekend': {
      const weekStart = startOfWeek(today);
      const saturday = addDays(weekStart, 6);
      const sunday = addDays(weekStart, 7);
      return [saturday, sunday];
    }

    case 'next_week': {
      const nextMonday = addDays(startOfWeek(today), 7);
      const nextFriday = addDays(nextMonday, 4);
      return eachDayOfInterval({ start: nextMonday, end: nextFriday });
    }

    case 'custom': {
      if (timeframeStart && timeframeEnd) {
        const start = new Date(timeframeStart);
        const end = new Date(timeframeEnd);
        return eachDayOfInterval({ start, end });
      }
      // Fallback to next 7 days
      return eachDayOfInterval({
        start: today,
        end: addDays(today, 6),
      });
    }

    default:
      return [today];
  }
};

/**
 * Get day label (Today, Tomorrow, or day name)
 */
const getDayLabel = (date: Date, now: Date): string => {
  const today = startOfDay(now);
  const tomorrow = addDays(today, 1);

  if (isSameDay(date, today)) return 'Today';
  if (isSameDay(date, tomorrow)) return 'Tomorrow';
  return format(date, 'EEEE'); // Day name (Monday, Tuesday, etc.)
};

/**
 * Promotional Card Variants
 */

// Option 1: Bold & Benefit-Driven
const PromoCard1: React.FC<{ onClose: () => void }> = ({ onClose }) => (
  <div
    className="w-full mb-4 bg-gradient-to-br from-cream to-white rounded-2xl shadow-xl shadow-primary/20 p-5 flex items-start justify-between border-2 border-primary/20 relative"
    style={{ animation: 'slideUp 0.3s ease-out' }}
  >
    <div className="flex-1 pr-4">
      <div className="text-4xl mb-2">📱</div>
      <h3 className="text-lg text-text-main font-extrabold mb-1">
        Skip the back-and-forth
      </h3>
      <p className="text-sm text-text-light font-semibold mb-3">
        Seamless scheduling, zero friction
      </p>
      <button
        className="text-sm bg-primary hover:bg-primary/90 text-white font-bold py-2.5 px-5 rounded-xl transition-all shadow-md hover:shadow-lg active:scale-95"
        onClick={() => window.open('https://catchupai.ai', '_blank')}
      >
        Get the app
      </button>
    </div>
    <button
      className="text-text-light hover:text-text-main p-1 -mr-2 -mt-2 rounded-full hover:bg-cream transition-colors"
      onClick={onClose}
      aria-label="Close promotional card"
    >
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
      </svg>
    </button>
  </div>
);

// Option 2: Minimal & Elegant
const PromoCard2: React.FC<{ onClose: () => void }> = ({ onClose }) => (
  <div
    className="w-full mb-4 bg-white rounded-3xl shadow-sm shadow-cream-dark/30 p-8 flex items-start justify-between border border-cream-dark relative"
    style={{ animation: 'slideUp 0.3s ease-out' }}
  >
    <div className="flex-1 pr-4">
      <h3 className="text-2xl text-text-main font-display font-bold mb-4 tracking-tight">
        Never send another link
      </h3>
      <button
        className="text-sm bg-cream hover:bg-cream-dark text-text-main font-bold py-2.5 px-5 rounded-xl transition-colors"
        onClick={() => window.open('https://catchupai.ai', '_blank')}
      >
        Download CatchUp
      </button>
    </div>
    <button
      className="text-text-light hover:text-text-main p-1 -mr-2 -mt-2 rounded-full hover:bg-cream transition-colors"
      onClick={onClose}
      aria-label="Close promotional card"
    >
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
      </svg>
    </button>
  </div>
);

// Option 3: Social Proof
const PromoCard3: React.FC<{ onClose: () => void }> = ({ onClose }) => (
  <div
    className="w-full mb-4 bg-white rounded-2xl shadow-xl shadow-stone-200/50 p-5 flex items-start justify-between border border-cream-dark ring-1 ring-primary/10 relative"
    style={{ animation: 'slideUp 0.3s ease-out' }}
  >
    <div className="flex-1 pr-4">
      <div className="flex items-center gap-1 mb-2">
        {[1, 2, 3, 4, 5].map((star) => (
          <span key={star} className="text-primary text-lg">★</span>
        ))}
      </div>
      <h3 className="text-sm text-text-main font-bold mb-1">
        Join 10,000+ easy schedulers
      </h3>
      <p className="text-xs text-text-light font-semibold mb-3">
        Stop sending links. Start planning faster.
      </p>
      <button
        className="text-xs bg-primary hover:bg-primary/90 text-white font-bold py-2 px-4 rounded-xl transition-all shadow-sm hover:shadow-md active:scale-95"
        onClick={() => window.open('https://catchupai.ai', '_blank')}
      >
        Get CatchUp now
      </button>
    </div>
    <button
      className="text-text-light hover:text-text-main p-1 -mr-2 -mt-2 rounded-full hover:bg-cream transition-colors"
      onClick={onClose}
      aria-label="Close promotional card"
    >
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
      </svg>
    </button>
  </div>
);

// Option 4: Playful & Conversational
const PromoCard4: React.FC<{ onClose: () => void }> = ({ onClose }) => (
  <div
    className="w-full mb-4 bg-cream/50 rounded-3xl shadow-lg shadow-cream-dark/30 p-5 flex items-start justify-between border-2 border-cream-dark relative backdrop-blur-sm"
    style={{ animation: 'slideUp 0.3s ease-out' }}
  >
    <div className="flex-1 pr-4">
      <div className="flex items-center gap-2 mb-2">
        <span className="text-2xl">✨</span>
        <h3 className="text-base text-text-main font-bold">
          That was easy, right?
        </h3>
      </div>
      <p className="text-sm text-text-light font-semibold mb-3">
        Make this easier next time with CatchUp
      </p>
      <button
        className="text-sm bg-white hover:bg-cream text-text-main font-bold py-2.5 px-5 rounded-2xl transition-all shadow-sm hover:shadow-md active:scale-95"
        onClick={() => window.open('https://catchupai.ai', '_blank')}
      >
        Get the app 🙌
      </button>
    </div>
    <button
      className="text-text-light hover:text-text-main p-1 -mr-2 -mt-2 rounded-full hover:bg-cream transition-colors"
      onClick={onClose}
      aria-label="Close promotional card"
    >
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
      </svg>
    </button>
  </div>
);

/**
 * AvailabilityPicker Component
 */
export const AvailabilityPicker: React.FC<AvailabilityPickerProps> = ({
  event,
  selectedSlots,
  onSlotsChange,
  onSubmit,
  isEditing,
  mode,
  organizerName,
}) => {
  const now = useMemo(() => new Date(), []);

  // Generate day groups with slots
  const dayGroups = useMemo<DayGroup[]>(() => {
    const days = generateDays(
      event.timeframe,
      event.timeframeStart,
      event.timeframeEnd
    );

    return days.map((date) => ({
      date,
      label: getDayLabel(date, now),
      dateLabel: format(date, 'MMM d'),
      slots: generateSlotsForDay(date, now),
    }));
  }, [event.timeframe, event.timeframeStart, event.timeframeEnd, now]);

  // Check if a slot is selected
  const isSlotSelected = useCallback(
    (slot: TimeSlot): boolean => {
      const slotStart = slot.start.getTime();
      const slotEnd = slot.end.getTime();

      return selectedSlots.some(
        (selected) => selected.start === slotStart && selected.end === slotEnd
      );
    },
    [selectedSlots]
  );

  // Toggle slot selection
  const toggleSlot = useCallback(
    (slot: TimeSlot) => {
      if (slot.isPast) return; // Don't allow selecting past slots

      const slotStart = slot.start.getTime();
      const slotEnd = slot.end.getTime();
      const isSelected = isSlotSelected(slot);

      if (isSelected) {
        // Remove slot
        onSlotsChange(
          selectedSlots.filter(
            (s) => s.start !== slotStart || s.end !== slotEnd
          )
        );
      } else {
        // Add slot
        onSlotsChange([
          ...selectedSlots,
          { start: slotStart, end: slotEnd },
        ]);
      }
    },
    [selectedSlots, onSlotsChange, isSlotSelected]
  );

  const selectedCount = selectedSlots.length;
  const isSubmitDisabled = selectedCount === 0;
  const [showPromoCard, setShowPromoCard] = useState(true);
  // Promo card variant (1-4). Change this value to test different designs.
  // To test variants: change useState(1) to useState(2), useState(3), or useState(4)
  const [promoVariant] = useState(1);

  return (
    <div className="font-display antialiased text-text-main h-screen flex flex-col overflow-hidden bg-cream">
      {/* Header */}
      <header className="relative shrink-0 w-full z-20 bg-cream pt-12 pb-4 px-6">
        <div className="space-y-2">
          <h1 className="text-3xl font-extrabold tracking-tight text-text-main">
            When are you free?
          </h1>
          <p className="text-lg text-text-light font-semibold leading-snug">
            {mode === 'group' && organizerName
              ? `${organizerName} is trying to find a time that works for everyone`
              : 'Pick all the times that work for you.'}
          </p>
        </div>
      </header>

      {/* Scrollable content */}
      <main className="flex-1 overflow-y-auto no-scrollbar relative z-10 px-6 pb-48" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
        {/* Time slots by day */}
        <div className="space-y-8 mt-4">
          {dayGroups.map((dayGroup) => (
            <section key={dayGroup.dateLabel}>
              <h2 className="text-xl font-bold text-text-main mb-4 flex items-baseline gap-2 sticky top-0 bg-cream/95 backdrop-blur-sm z-10 py-3">
                {dayGroup.label}
                <span className="text-sm font-bold text-text-light">
                  {dayGroup.dateLabel}
                </span>
              </h2>

              <div className="grid grid-cols-3 gap-3">
                {dayGroup.slots.map((slot, index) => {
                  const isSelected = isSlotSelected(slot);
                  const isPast = slot.isPast;

                  return (
                    <button
                      key={`${dayGroup.dateLabel}-${index}`}
                      onClick={() => toggleSlot(slot)}
                      disabled={isPast}
                      className={`
                        py-3 px-1 rounded-2xl text-sm font-bold transition-all
                        ${
                          isPast
                            ? 'bg-cream-dark/30 text-gray-300 cursor-not-allowed border border-transparent'
                            : isSelected
                            ? 'bg-primary text-white shadow-lg shadow-primary/30 ring-2 ring-primary ring-offset-2 ring-offset-cream active:scale-95'
                            : 'bg-white border-2 border-cream-dark text-text-main active:scale-95 hover:border-primary/40 hover:text-primary'
                        }
                      `}
                    >
                      <span className="relative z-10">{slot.label}</span>
                    </button>
                  );
                })}
              </div>
            </section>
          ))}

          {/* Helper text */}
          <div className="pt-2 pb-8 text-center px-6">
            <p className="text-text-light text-sm font-bold opacity-80">
              Just pick times you actually can make — CatchUp handles the rest.
            </p>
          </div>
        </div>
      </main>

      {/* Fixed bottom section with promo card and submit button */}
      <div className="fixed bottom-0 left-0 right-0 z-30 pointer-events-none">
        {/* Gradient overlay */}
        <div className="absolute bottom-0 inset-x-0 h-48 bg-gradient-to-t from-cream via-cream/95 to-transparent"></div>

        {/* Content */}
        <div className="relative w-full px-4 pb-4 pointer-events-auto flex flex-col items-center" style={{ paddingBottom: 'max(1rem, env(safe-area-inset-bottom))' }}>
          {/* Promotional card - Render selected variant */}
          {showPromoCard && (
            <>
              {promoVariant === 1 && <PromoCard1 onClose={() => setShowPromoCard(false)} />}
              {promoVariant === 2 && <PromoCard2 onClose={() => setShowPromoCard(false)} />}
              {promoVariant === 3 && <PromoCard3 onClose={() => setShowPromoCard(false)} />}
              {promoVariant === 4 && <PromoCard4 onClose={() => setShowPromoCard(false)} />}
            </>
          )}

          {/* Submit button */}
          <button
            onClick={onSubmit}
            disabled={isSubmitDisabled}
            className={`
              w-full relative flex items-center justify-center rounded-2xl py-4 shadow-xl transition-all
              ${
                isSubmitDisabled
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed shadow-gray-300/25'
                  : 'bg-primary text-white shadow-primary/25 active:scale-[0.98] hover:bg-primary-dark'
              }
            `}
          >
            <span className="text-lg font-extrabold tracking-wide">
              {isEditing
                ? selectedCount > 0
                  ? `Update availability (${selectedCount})`
                  : 'Update availability'
                : selectedCount > 0
                ? `Save my availability (${selectedCount})`
                : 'Save my availability'}
            </span>
          </button>
        </div>
      </div>

      <style>{`
        @keyframes slideUp {
          0% {
            transform: translateY(20px);
            opacity: 0;
          }
          100% {
            transform: translateY(0);
            opacity: 1;
          }
        }
      `}</style>
    </div>
  );
};
